import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  UserSubscription,
} from 'types';

export type StripeCheckoutOutcome = 'success' | 'failure' | null;
export type StripeCheckoutFlow = 'onboarding' | 'subscription';

type UseStripeCheckoutReturnParams = {
  subscription: UserSubscription | null | undefined;
};

export function useStripeCheckoutReturn({
  subscription,
}: UseStripeCheckoutReturnParams) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  /** After 10s of polling, show success outcome with fallback plan label. */
  const [checkoutSyncTimedOut, setCheckoutSyncTimedOut] = useState(false);

  const stripeOutcome = useMemo((): StripeCheckoutOutcome => {
    if (searchParams.get('stripe_success') === '1') return 'success';
    if (
      searchParams.get('stripe_cancel') === '1' ||
      searchParams.get('canceled') === 'true'
    ) {
      return 'failure';
    }
    return null;
  }, [searchParams]);

  const checkoutStripeFlow = useMemo(
    (): StripeCheckoutFlow =>
      searchParams.get('checkout') === 'onboarding'
        ? 'onboarding'
        : 'subscription',
    [searchParams],
  );

  const sessionIdFromUrl = searchParams.get('session_id');
  const checkoutPlanIdFromUrl = searchParams.get('checkout_plan_id');
  const checkoutFrequencyFromUrl = searchParams.get('checkout_frequency');

  const subscriptionReturnExpectsSync =
    checkoutStripeFlow === 'subscription' &&
    Boolean(checkoutPlanIdFromUrl) &&
    Boolean(checkoutFrequencyFromUrl);

  const subscriptionCheckoutSynced = useMemo(() => {
    if (!checkoutPlanIdFromUrl || !checkoutFrequencyFromUrl) return false;
    const freqOk =
      checkoutFrequencyFromUrl === SubscriptionFrequency.MONTH ||
      checkoutFrequencyFromUrl === SubscriptionFrequency.YEAR;
    if (!freqOk) return false;
    const sub = subscription;
    if (!sub?.id) return false;
    if (sub.subscriptionPlanId !== checkoutPlanIdFromUrl) return false;
    if (sub.frequency !== checkoutFrequencyFromUrl) return false;
    if (!sub.subscriptionId?.trim()) return false;
    if (sub.status !== SubscriptionStatus.ACTIVE) return false;
    return true;
  }, [checkoutPlanIdFromUrl, checkoutFrequencyFromUrl, subscription]);

  useEffect(() => {
    if (stripeOutcome !== 'success') {
      setCheckoutSyncTimedOut(false);
    }
  }, [stripeOutcome]);

  const clearStripeQuery = useCallback(() => {
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [navigate, location.pathname]);

  const checkoutPlanLabel =
    subscription?.subscriptionPlan?.productName ||
    subscription?.subscriptionPlan?.name;

  const onboardingOutcomePlanLabel = useMemo(() => {
    if (checkoutStripeFlow !== 'onboarding' || !sessionIdFromUrl) {
      return undefined;
    }
    const list = subscription?.onboardingPurchases;
    if (!list?.length) return undefined;
    const hit = list.find(
      (p) => p.stripeCheckoutSessionId === sessionIdFromUrl,
    );
    return hit?.productName || hit?.slug || undefined;
  }, [checkoutStripeFlow, sessionIdFromUrl, subscription]);

  useEffect(() => {
    if (stripeOutcome !== 'success') return undefined;

    const runExponentialPoll = () => {
      let cancelled = false;
      const timeoutIds: number[] = [];
      const start = Date.now();
      const MAX_MS = 10000;

      const fetchOnce = () => {
        if (!cancelled) {
          dispatch(getCurrentUserSubscription.trigger());
        }
      };

      const clearAll = () => {
        while (timeoutIds.length) {
          const id = timeoutIds.pop();
          if (id != null) window.clearTimeout(id);
        }
      };

      fetchOnce();
      let nextDelayMs = 1000;

      const scheduleChain = () => {
        if (cancelled) return;
        const elapsed = Date.now() - start;
        if (elapsed >= MAX_MS) {
          setCheckoutSyncTimedOut(true);
          return;
        }
        const wait = Math.min(nextDelayMs, MAX_MS - elapsed);
        const id = window.setTimeout(() => {
          if (cancelled) return;
          fetchOnce();
          nextDelayMs *= 2;
          const el2 = Date.now() - start;
          if (el2 >= MAX_MS) {
            setCheckoutSyncTimedOut(true);
          } else {
            scheduleChain();
          }
        }, wait);
        timeoutIds.push(id);
      };

      scheduleChain();

      return () => {
        cancelled = true;
        clearAll();
      };
    };

    if (subscriptionReturnExpectsSync) {
      if (subscriptionCheckoutSynced) {
        dispatch(getCurrentUserSubscription.trigger());
        return undefined;
      }
      if (checkoutSyncTimedOut) {
        return undefined;
      }
      return runExponentialPoll();
    }

    if (checkoutStripeFlow === 'onboarding' && sessionIdFromUrl) {
      if (onboardingOutcomePlanLabel) {
        dispatch(getCurrentUserSubscription.trigger());
        return undefined;
      }
      if (checkoutSyncTimedOut) {
        return undefined;
      }
      return runExponentialPoll();
    }

    dispatch(getCurrentUserSubscription.trigger());
    const delayed = window.setTimeout(() => {
      dispatch(getCurrentUserSubscription.trigger());
    }, 2500);
    return () => {
      window.clearTimeout(delayed);
    };
  }, [
    stripeOutcome,
    checkoutStripeFlow,
    sessionIdFromUrl,
    onboardingOutcomePlanLabel,
    subscriptionReturnExpectsSync,
    subscriptionCheckoutSynced,
    checkoutSyncTimedOut,
    dispatch,
  ]);

  const showOnboardingConfirmingLoader =
    stripeOutcome === 'success' &&
    checkoutStripeFlow === 'onboarding' &&
    Boolean(sessionIdFromUrl) &&
    !onboardingOutcomePlanLabel &&
    !checkoutSyncTimedOut;

  const showSubscriptionConfirmingLoader =
    stripeOutcome === 'success' &&
    subscriptionReturnExpectsSync &&
    !subscriptionCheckoutSynced &&
    !checkoutSyncTimedOut;

  const showCheckoutConfirmingLoader =
    showOnboardingConfirmingLoader || showSubscriptionConfirmingLoader;

  /** Avoid full-screen loader stacking during Stripe return refetch (flicker). */
  const suppressFloatingRefetchLoader =
    stripeOutcome === 'success' &&
    ((checkoutStripeFlow === 'onboarding' && Boolean(sessionIdFromUrl)) ||
      subscriptionReturnExpectsSync);

  return {
    stripeOutcome,
    checkoutStripeFlow,
    sessionIdFromUrl,
    checkoutPlanLabel,
    onboardingOutcomePlanLabel,
    clearStripeQuery,
    showCheckoutConfirmingLoader,
    suppressFloatingRefetchLoader,
    subscriptionReturnExpectsSync,
  };
}
